import { Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner } from '@lensora/ui'
import { useQuery } from '@tanstack/react-query'


import { api } from '@/lib/api'

type StatsResponse = {
  tenantCount: number
  userCounts: Record<string, number>
  llmQueries30d: number
  creditsUsed30d: number
}

export function AdminOverviewPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['lensora-admin', 'stats'],
    queryFn: () => api.get<StatsResponse>('/api/lensora-admin/stats'),
  })

  if (isLoading) {
    return <div className="flex h-40 items-center justify-center"><Spinner /></div>
  }

  const totalUsers = data ? Object.values(data.userCounts).reduce((a, b) => a + b, 0) : 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text">Platform Overview</h1>
        <p className="mt-1 text-sm text-text-muted">Stats across all Lensora tenants</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard title="Tenants" value={String(data?.tenantCount ?? 0)} description="Active workspaces" />
        <StatCard title="Total users" value={String(totalUsers)} description="Across all tenants" />
        <StatCard title="LLM queries (30d)" value={String(data?.llmQueries30d ?? 0)} description="AI queries run" />
        <StatCard title="Credits used (30d)" value={String(data?.creditsUsed30d ?? 0)} description="Credits consumed" />
      </div>

      {data?.userCounts && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Users by role</CardTitle>
            <CardDescription>Breakdown across all tenants</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {(['owner', 'admin', 'member', 'client'] as const).map((role) => (
                <div key={role} className="rounded-lg border border-border p-3 text-center">
                  <p className="text-2xl font-bold text-text">{data.userCounts[role] ?? 0}</p>
                  <p className="mt-1 text-xs capitalize text-text-muted">{role}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function StatCard({ title, value, description }: { title: string; value: string; description: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-text-muted">{description}</p>
      </CardContent>
    </Card>
  )
}
